import { CompanyId, CompanyName, Order, RoleSortItem } from "helpers";
import { useUser } from "hooks";
import { Dispatch, ReactNode, SetStateAction, createContext, useContext, useEffect, useState } from "react";
import {
  RolesWithDetailsResponse,
  useGetPermissionsByGroupQuery,
  useLazyGetCompaniesQuery,
  useLazyGetRolesWithDetailsQuery,
} from "services";

interface Params {
  search?: string;
  sort?: RoleSortItem;
  order?: Order;
}

const initialParams: Params = {
  search: undefined,
  sort: undefined,
  order: undefined,
};

export interface RoleManagementContextValues {
  companyId: CompanyId;
  companyName: CompanyName;
  data: RolesWithDetailsResponse | undefined;
  isLoading: boolean;
  isFetching: boolean;
  isSuccess: boolean;
  params: Params;
  page: number;
  setPage: Dispatch<SetStateAction<number>>;
  onFetch: (preferCacheValue?: boolean) => void;
  onSort: (sort: RoleSortItem, order?: Order) => void;
  onSearch: (search: string) => void;
  onChangeCompany: (search: string) => void;
}

export const RoleManagementContext = createContext<RoleManagementContextValues | undefined>(undefined);

export const RoleManagementProvider = ({ children }: { children: ReactNode }) => {
  const { companyId, setCompanyId } = useUser();
  const [trigger, result] = useLazyGetRolesWithDetailsQuery();
  const [getCompanies] = useLazyGetCompaniesQuery();
  const [companyName, setCompanyName] = useState<CompanyName>("");
  const [params, setParams] = useState<Params>(initialParams);
  const [page, setPage] = useState<number>(0);
  const pageSize = 4;

  // getting data earlier!
  const { data } = useGetPermissionsByGroupQuery({});

  // Update company name when companyId changes
  useEffect(() => {
    if (companyId === "0") {
      setCompanyName("");
    } else if (companyId) {
      // Fetch company name from API
      getCompanies({ page: 0, pageSize: 100 })
        .unwrap()
        .then((response) => {
          const company = response.companies?.find((c) => c.company_id === companyId);
          setCompanyName(company?.name || companyId);
        })
        .catch(() => {
          setCompanyName(companyId); // fallback to ID if fetch fails
        });
    }
  }, [companyId, getCompanies]);

  useEffect(() => {
    !!companyId && onFetchHandler();
  }, [companyId, params, page]);

  const onSortHandler = (sort: RoleSortItem, order?: Order) => {
    let sortValue: RoleSortItem | undefined;
    let orderValue: Order | undefined;

    // in case we don't have separate sorts for them
    if (sort === "name-a-z") {
      sortValue = "role-name";
      orderValue = "ASC";
    }
    if (sort === "name-z-a") {
      sortValue = "role-name";
      orderValue = "DESC";
    }
    if (sort === "latest") {
      sortValue = "latest";
      orderValue = "DESC";
    }

    setPage(0);
    setParams((oldObject) => ({ ...oldObject, sort: sortValue || sort, order: orderValue || order }));
  };

  const onSearchHandler = (search: string) => {
    // This needs to be revisited later!!
    setPage(0);
    setParams({ search });
  };

  const onChangeCompanyHandler = (company_id: CompanyId, companyName?: string) => {
    setPage(0);
    setCompanyId(!!company_id ? company_id : "0");
    setCompanyName(companyName || company_id);
    setParams(initialParams);
  };

  const onFetchHandler = (preferCacheValue: boolean = false) =>
    trigger({ company_id: companyId, page, pageSize, ...params }, preferCacheValue);

  const values: RoleManagementContextValues = {
    companyId,
    companyName: companyId === "0" ? "All Companies" : companyName,
    data: result?.data,
    isLoading: result.isLoading,
    isSuccess: result.isSuccess,
    isFetching: result.isFetching,
    params,
    page,
    setPage,
    onFetch: onFetchHandler,
    onSort: onSortHandler,
    onSearch: onSearchHandler,
    onChangeCompany: setCompanyId,
  };

  return <RoleManagementContext.Provider value={values}>{children}</RoleManagementContext.Provider>;
};

export const useRoleManagementContext = () => {
  const context = useContext(RoleManagementContext);

  if (!context) {
    throw new Error("useRoleManagementContext must be used within a RoleManagementProvider!");
  }
  return context;
};
